import { ArrowDown, ArrowDownUp, ArrowUp, LayoutGrid, Rows3 } from "lucide-react";
import { useState } from "react";

import { PipelineGrid } from "@/components/pipeline/PipelineGrid";

import { daysSince, euros, frDate, initials, isDormant, isOverdue, STATUS_LABELS, visibleTags } from "@/components/pipeline/utils";

const COLS = [
  { id: "title", label: "Opportunité" }, { id: null, label: "Pipeline · étape" }, { id: null, label: "Commercial" },
  { id: "amount", label: "Montant" }, { id: null, label: "Statut" }, { id: "remind", label: "Relance" }, { id: "created", label: "Créée le" }, { id: "updated", label: "Dernière activité" }, { id: null, label: "Tags" },
];

const VIEW_KEY = "airthermo-list-view";
const DESC_FIRST = ["created", "updated", "amount"];
const SortIcon = ({ id, sort }) => {
  if (sort.replace("-", "") !== id) return <ArrowDownUp size={11} />;
  const desc = DESC_FIRST.includes(id) !== sort.startsWith("-");
  return desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />;
};

export const PipelineList = ({ data, sort, setSort, page, setPage, selected, setSelected, dormantDays, onOpen }) => {
  const ids = data.rows.map((r) => r.lead_id);
  const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const pages = Math.max(1, Math.ceil(data.total / 50));
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");
  const pick = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  return (
    <div className="pl-list" data-view="customers" data-testid="pipeline-list-table">
      <div className="toolbar ldp-list-bar">
        <label className="ldp-all"><input type="checkbox" className="check" checked={allOn} onChange={() => setSelected(allOn ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])])} data-testid="table-select-all" /> Tout sélectionner</label>
        {view === "grid" && (
          <span className="ldp-sorts">Trier :{COLS.filter((c) => c.id).map((c) => <button key={c.id} type="button" className="chip" aria-pressed={sort.replace("-", "") === c.id} onClick={() => setSort(sort === c.id ? `-${c.id}` : c.id)} data-testid={`sort-${c.id}`}>{c.label} <SortIcon id={c.id} sort={sort} /></button>)}</span>
        )}
        <span className="toolbar__spacer" />
        <div className="seg ldp-seg" role="group" aria-label="Affichage">
          <button type="button" className="seg__btn" aria-pressed={view === "grid"} onClick={() => pick("grid")} data-testid="list-view-grid"><LayoutGrid size={13} /> Grille</button>
          <button type="button" className="seg__btn" aria-pressed={view === "table"} onClick={() => pick("table")} data-testid="list-view-table"><Rows3 size={13} /> Tableau</button>
        </div>
      </div>
      {view === "grid" ? <PipelineGrid rows={data.rows} selected={selected} toggle={toggle} onOpen={onOpen} dormantDays={dormantDays} /> : (
      <div className="agent-table-wrap">
        <table className="agent-table pl-table">
          <thead>
            <tr>
              <th />
              {COLS.map((c) => (
                <th key={c.label}>
                  {c.id ? <button type="button" className={`pl-sort${sort.replace("-", "") === c.id ? " on" : ""}`} onClick={() => setSort(sort === c.id ? `-${c.id}` : c.id)} data-testid={`th-sort-${c.id}`}>{c.label} <SortIcon id={c.id} sort={sort} /></button> : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.lead_id} data-lead-id={r.lead_id} className={selected.includes(r.lead_id) ? "is-selected" : ""} data-testid={`pipeline-row-${r.lead_id}`}>
                <td><input type="checkbox" checked={selected.includes(r.lead_id)} onChange={() => toggle(r.lead_id)} data-testid={`table-row-checkbox-${r.lead_id}`} /></td>
                <td><button type="button" className="pl-row-title" onClick={() => onOpen(r.lead_id)} data-testid={`pipeline-row-open-${r.lead_id}`}>{r.title}</button></td>
                <td><span className="pl-muted">{r.pipeline}</span><br />{r.step}</td>
                <td><span className="pl-owner"><span className="pl-avatar sm">{initials(r.owner_name)}</span>{r.owner_name || "—"}</span></td>
                <td>{euros(r.amount) || "—"}</td>
                <td><span className={`pl-status s-${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span></td>
                <td>{r.remind_date ? <span className={`pl-badge ${isOverdue(r) ? "danger" : "info"}`}>{frDate(r.remind_date)}</span> : "—"}</td>
                <td data-testid={`pipeline-row-created-${r.lead_id}`}>{r.created_at ? frDate(r.created_at.slice(0, 10)) : "—"}</td>
                <td>{isDormant(r, dormantDays) ? <span className="pl-badge warn">{daysSince(r.updated_at)} j</span> : <span className="pl-muted">il y a {daysSince(r.updated_at)} j</span>}</td>
                <td><div className="pl-tags">{visibleTags(r.tags).slice(0, 3).map((t) => <span key={t} className="pl-tag">{t}</span>)}</div></td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={10} className="pl-empty">Aucune opportunité ne correspond à ces filtres.</td></tr>}
          </tbody>
        </table>
      </div>
      )}
      <div className="pl-pager">
        <span className="pl-muted">{data.total.toLocaleString("fr-FR")} opportunités{data.amount > 0 ? ` · ${euros(data.amount)}` : ""}</span>
        <button type="button" className="secondary-button" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="list-prev">Précédent</button>
        <span>Page {page + 1} / {pages}</span>
        <button type="button" className="secondary-button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="list-next">Suivant</button>
      </div>
    </div>
  );
};
